import { Component, Input, OnInit } from '@angular/core';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { Subject } from 'rxjs';
import * as moment from 'moment';
import { CustomerMembership } from '../../../../../../../../../models/membership';
import { propExists } from '../../../../../../../../../utils';
import { CustomerMembershipStatus } from '../../../../../../../../../models/membership-enums';

enum ActionList {
  cancelImmediately,
  cancelFutureBillingDate,
  undoCancelFutureBillingDate,
  freeze,
  freezeIndividual,
  undoScheduledFreeze,
  undoScheduledFreezeIndividual,
  unfreeze,
  scheduleUnfreeze,
  scheduleUnfreezeIndividual,
  undoScheduledUnfreeze,
  undoScheduledUnfreezeIndividual,
  editStartDate,
  editBillDate,
  editAddOnsImmediately,
  editAddOnsNextBillDate,
  reassign,
  editMembers,
  addMemberNextBillDate,
  removeMemberNextBillDate,
  editRecurringDiscount,
  editOneOffDiscount,
  fixFailedPayment,
  changePaymentMethod,
  changeRoster,
  cancelUnactivated,
  addPaymentMethod,
  purchaseUpfrontPaymentOption,
}

/**
 * The only actions offered for a membership that was paid for but is awaiting a
 * payment method (PRO-1460). It has never been active and never billed, so
 * every other action is either meaningless (freeze, discounts, add-ons) or
 * would edit a membership the gym may be about to cancel. Staff either give it
 * a payment method so it activates, or cancel the purchase.
 */
const AWAITING_PAYMENT_METHOD_ACTIONS = [
  ActionList.cancelUnactivated,
  ActionList.addPaymentMethod
];

@Component({
  selector: 'app-edit-recurring-customer-membership',
  templateUrl: './edit-recurring-customer-membership-modal.component.html',
  styleUrls: ['./edit-recurring-customer-membership-modal.component.css']
})
export class EditRecurringCustomerMembershipModalComponent implements OnInit {

  @Input() membership: CustomerMembership;
  @Input() savedCallback: (successMessage: string) => undefined;

  public selectedAction: ActionList = null;

  public saveClickedSubject = new Subject<void>();
  public loading = false;
  public serverError: number = null;

  public ActionList = ActionList;

  constructor(
    public activeModal: NgbActiveModal
  ) {}

  ngOnInit() {
  }

  /**
   * Whether the membership has been bought but has never been active — either
   * because its first payment has not gone through, or because it went through
   * but no payment method was saved to bill it with (PRO-1460).
   *
   * Actions that operate on a running membership (cancel, freeze, unfreeze)
   * are hidden for both.
   */
  private isPreActivation(): boolean {
    return [
      CustomerMembershipStatus.awaitingInitialPayment,
      CustomerMembershipStatus.awaitingPaymentMethod
    ].includes(this.membership.status);
  }

  /**
   * Whether any action at all is offered for this membership. An ended
   * membership may have none, in which case the modal explains that instead
   * of showing an empty action list (PRO-1500).
   */
  public hasAvailableActions(): boolean {
    return Object.values(ActionList)
      .filter((action): action is ActionList => typeof action === 'number')
      .some((action) => this.actionIsAvailable(action));
  }

  public actionIsAvailable(action: ActionList) {
    // An allowlist rather than a subtraction from every case below, so a
    // newly added action is hidden from never-active memberships by default.
    if (this.membership.status === CustomerMembershipStatus.awaitingPaymentMethod) {
      return AWAITING_PAYMENT_METHOD_ACTIONS.includes(action);
    }

    switch (action) {
      case ActionList.cancelImmediately:
        return (
          ![
            CustomerMembershipStatus.deniedStaffApproval,
            CustomerMembershipStatus.ended
          ].includes(this.membership.status) &&
          (!this.isPreActivation())
        );
      case ActionList.cancelFutureBillingDate:
        return (
          ![
            CustomerMembershipStatus.deniedStaffApproval,
            CustomerMembershipStatus.ended
          ].includes(this.membership.status) &&
          (!propExists(this.membership.membership_cancel_date)) &&
          (!propExists(this.membership.membership_freeze_date)) &&
          this.membership.has_started
        );
      case ActionList.undoCancelFutureBillingDate:
        return propExists(this.membership.membership_cancel_date);
      case ActionList.freeze:
        return (
          ![
            CustomerMembershipStatus.deniedStaffApproval,
            CustomerMembershipStatus.ended
          ].includes(this.membership.status) &&
          (!this.isPreActivation()) &&
          (this.membership.status !== CustomerMembershipStatus.frozen) &&
          (!propExists(this.membership.membership_cancel_date)) &&
          (!propExists(this.membership.membership_freeze_date)) &&
          this.membership.has_started
        );
      case ActionList.freezeIndividual:
        // Ensure that the membership as a whole allows individual freezing.
        // A single-member group membership cannot be individually frozen — the
        // membership-level Freeze action should be used instead.
        const membershipAllows = (
          this.membership.is_group &&
          (this.membership.all_customers.length > 1) &&
          ![
            CustomerMembershipStatus.deniedStaffApproval,
            CustomerMembershipStatus.ended
          ].includes(this.membership.status) &&
          (!this.isPreActivation()) &&
          (this.membership.status !== CustomerMembershipStatus.frozen) &&
          (!propExists(this.membership.membership_cancel_date)) &&
          this.membership.has_started
        )
        if (!membershipAllows) { return false; }

        // Ensure that at least one member is not already individually frozen or scheduled to freeze.
        for (const member of this.membership.all_customers) {
          if ((!member.is_individually_frozen) && (member.member_freeze_date === null)) {
            return true;
          }
        }
        return false;
      case ActionList.undoScheduledFreeze:
        return propExists(this.membership.membership_freeze_date);
      case ActionList.undoScheduledFreezeIndividual:
        for (const member of this.membership.all_customers) {
          if (propExists(member.member_freeze_date)) { return true; }
        }
        return false;
      case ActionList.unfreeze:
        return this.membership.status === CustomerMembershipStatus.frozen;
      case ActionList.scheduleUnfreeze:
        return (
          [
            CustomerMembershipStatus.frozen,
            CustomerMembershipStatus.freezePaymentFailed,
            CustomerMembershipStatus.freezePaymentProcessing,
            CustomerMembershipStatus.freezePaymentPending
          ].includes(this.membership.status) &&
          (!propExists(this.membership.membership_unfreeze_date))
        );
      case ActionList.scheduleUnfreezeIndividual:
        for (const member of this.membership.all_customers) {
          if (member.is_individually_frozen && !propExists(member.member_unfreeze_date)) { return true; }
        }
        return false;
      case ActionList.undoScheduledUnfreeze:
        return propExists(this.membership.membership_unfreeze_date);
      case ActionList.undoScheduledUnfreezeIndividual:
        for (const member of this.membership.all_customers) {
          if (propExists(member.member_unfreeze_date)) { return true; }
        }
        return false;
      case ActionList.editStartDate:
        // Start date may be edited if the membership started at most 28 days
        // ago, has not ended (PRO-1500) and has not yet been billed for a
        // renewal or freeze fee (PRO-1504) - after that the end date belongs
        // to the renewal, and the server refuses the edit.
        return (
          ![
            CustomerMembershipStatus.deniedStaffApproval,
            CustomerMembershipStatus.ended
          ].includes(this.membership.status) &&
          !this.membership.has_been_renewed &&
          (moment(this.membership.start_date) >= moment().startOf('day').subtract(28, 'days'))
        );
      case ActionList.editBillDate:
        return (
          this.membership.status === CustomerMembershipStatus.active &&
          this.membership.has_started &&
          !this.membership.is_pending_staff_approval &&
          (!propExists(this.membership.membership_cancel_date)) &&
          (!propExists(this.membership.membership_freeze_date))
        );
      case ActionList.editAddOnsImmediately:
      case ActionList.editAddOnsNextBillDate:
        return (
          ![
            CustomerMembershipStatus.deniedStaffApproval,
            CustomerMembershipStatus.ended
          ].includes(this.membership.status) &&
          this.membership.customer_add_on_options.length > 0
        );
      case ActionList.reassign:
        return (
          ![
            CustomerMembershipStatus.deniedStaffApproval,
            CustomerMembershipStatus.ended
          ].includes(this.membership.status) &&
          !this.membership.is_group &&
          (this.membership.unlinked_customers.length === 0)
        );
      case ActionList.editMembers:
      case ActionList.addMemberNextBillDate:
      case ActionList.removeMemberNextBillDate:
        return (
          ![
            CustomerMembershipStatus.deniedStaffApproval,
            CustomerMembershipStatus.ended
          ].includes(this.membership.status) &&
          this.membership.is_group
        );
      case ActionList.editRecurringDiscount:
      case ActionList.editOneOffDiscount:
        return ![
          CustomerMembershipStatus.deniedStaffApproval,
          CustomerMembershipStatus.ended
        ].includes(this.membership.status);
      case ActionList.fixFailedPayment:
        return [
          CustomerMembershipStatus.renewalPaymentFailed,
          CustomerMembershipStatus.freezePaymentFailed
        ].includes(this.membership.status);
      case ActionList.purchaseUpfrontPaymentOption:
        return (
          this.membership.status === CustomerMembershipStatus.active &&
          this.membership.membership_has_upfront_payment_options
        );
      case ActionList.changePaymentMethod:
        return ![
          CustomerMembershipStatus.renewalPaymentFailed,
          CustomerMembershipStatus.freezePaymentFailed,
          CustomerMembershipStatus.deniedStaffApproval,
          CustomerMembershipStatus.ended,
          CustomerMembershipStatus.awaitingInitialPayment,
          CustomerMembershipStatus.awaitingPaymentMethod
        ].includes(this.membership.status);
      case ActionList.cancelUnactivated:
      case ActionList.addPaymentMethod:
        // Only offered via the awaiting-payment-method guard above (PRO-1460).
        return false;
      case ActionList.changeRoster:
        return (
          propExists(this.membership.rolling_event_type_roster_id) &&
          ![
            CustomerMembershipStatus.awaitingInitialPayment,
            CustomerMembershipStatus.awaitingPaymentMethod,
            CustomerMembershipStatus.initialPaymentCancelled,
            CustomerMembershipStatus.deniedStaffApproval,
            CustomerMembershipStatus.ended,
          ].includes(this.membership.status)
        );
    }
  }

  /** True when a scheduled cancel or freeze is the only reason Edit Bill Date is hidden. */
  public billDateBlockedByScheduledChange(): boolean {
    return (
      this.membership.status === CustomerMembershipStatus.active &&
      this.membership.has_started &&
      !this.membership.is_pending_staff_approval &&
      (propExists(this.membership.membership_cancel_date) || propExists(this.membership.membership_freeze_date))
    );
  }

  public getSubmitButtonText() {
    switch (this.selectedAction) {
      case ActionList.fixFailedPayment:
      case ActionList.changePaymentMethod:
      case ActionList.addPaymentMethod:
      case ActionList.purchaseUpfrontPaymentOption:
        return 'Open Climber App';
      default:
        return 'Save';
    }
  }

  public handleSubmit() {
    this.saveClickedSubject.next();
  }

}
